import {
  Box,
  CircularProgress,
} from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import minMax from 'dayjs/plugin/minMax';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import BaseHighchart from '../../BaseHighchart';
import { ChartDataType } from '../../types';
import { ReduxState } from '../../../../types';
import { navigateToChart } from '../../../../utils/helpers';
import ChartFilter from '../../ChartFilter';
import { getChartConfig, getYAxis, calculateYAxisLimits } from '../utils';
import {
  CHILL_PORTIONS_NAME,
  CHART_TITLE_PORCIONES_FRIO_ACUMULADAS,
} from '../../../../constants/graphs';
import apiClient, { chillPortionsConfig } from '../../../../requests/api/apiClient';
import {
  ChillPortionsChartProps,
  ChillPortionsConfigType,
  DEFAULT_CHILL_PORTIONS_CONFIG,
} from './types';
import { getPlotBands } from '../../utils';
import ChartConfigDialog from '../../ChartConfigDialog/ChartConfigDialog';
import ChillPortionsConfigContent from './ChillPortionsConfigContent';
import useChartConfigClick from '../../../../hooks/useChartConfigClick';

dayjs.extend(utc);
dayjs.extend(minMax);

function ChillPortionsChart({
  chartView,
  sectorId,
  farmId,
  hideLegend = false,
  hideXValues = false,
  hideYValues = false,
  height = 600,
  title = CHART_TITLE_PORCIONES_FRIO_ACUMULADAS,
  yAxisTitle = 'Porciones de Frío Acumuladas',
  refreshKey,
}: ChillPortionsChartProps) {
  const chartName = 'porciones-frio';

  const [chartData, setChartData] = useState<ChartDataType>({
    series: [],
    categories: [],
    absoluteCategories: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [activeConfig, setActiveConfig] = useState<ChillPortionsConfigType>(
    DEFAULT_CHILL_PORTIONS_CONFIG,
  );
  const [configLoaded, setConfigLoaded] = useState(false);
  const history = useHistory();

  const { interval } = useSelector((state: ReduxState) => state.graphsFilter);
  const selectedSector = useSelector((state: ReduxState) => state.currentSector.selectedSector);

  // Carga la temporada persistida del sector antes de pedir los datos del gráfico.
  const loadConfig = useCallback(async () => {
    const sectorKey = selectedSector?.id || sectorId;
    if (!sectorKey) {
      setConfigLoaded(true);
      return;
    }
    try {
      const { data } = await chillPortionsConfig.get(sectorKey);
      const saved = (data as { chillPortionsConfig?: Partial<ChillPortionsConfigType> })
        ?.chillPortionsConfig;
      setActiveConfig({
        chillPortionsStartMonth:
          saved?.chillPortionsStartMonth ?? DEFAULT_CHILL_PORTIONS_CONFIG.chillPortionsStartMonth,
        chillPortionsStartDay:
          saved?.chillPortionsStartDay ?? DEFAULT_CHILL_PORTIONS_CONFIG.chillPortionsStartDay,
        chillPortionsEndMonth:
          saved?.chillPortionsEndMonth ?? DEFAULT_CHILL_PORTIONS_CONFIG.chillPortionsEndMonth,
        chillPortionsEndDay:
          saved?.chillPortionsEndDay ?? DEFAULT_CHILL_PORTIONS_CONFIG.chillPortionsEndDay,
      });
    } catch (error) {
      console.error('Error fetching chill portions config:', error);
    } finally {
      setConfigLoaded(true);
    }
  }, [selectedSector, sectorId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Re-fetch saved config when settings page or dialog saves (refreshKey increments).
  useEffect(() => {
    if (refreshKey !== undefined && refreshKey > 0) {
      loadConfig();
    }
  }, [refreshKey]);

  const fetchConfigAndData = useCallback(async () => {
    setIsLoading(true);
    try {
      if (interval) {
        const { data } = await apiClient.readings.getChillPortionsChartData({
          farmId,
          startDate: interval.startDate,
          endDate: interval.endDate,
          sectorId,
          telemetryType: selectedSector?.telemetryType,
          sn: selectedSector?.telemetryType === 'sn' ? selectedSector.weatherZoneId : undefined,
          chillPortionsStartMonth: activeConfig.chillPortionsStartMonth,
          chillPortionsStartDay: activeConfig.chillPortionsStartDay,
          chillPortionsEndMonth: activeConfig.chillPortionsEndMonth,
          chillPortionsEndDay: activeConfig.chillPortionsEndDay,
        });

        setChartData({
          series: data.series,
          absoluteCategories: data.absoluteCategories,
        });
      }
    } catch (error) {
      console.error('Error fetching config/data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [farmId, sectorId, selectedSector, interval, activeConfig]);

  useEffect(() => {
    if (configLoaded) {
      fetchConfigAndData();
    }
  }, [fetchConfigAndData, configLoaded]);

  const chartConfig = useMemo(
    () => {
      if (!interval) {
        return undefined;
      }
      const yAxisLimits = calculateYAxisLimits(chartData.series);

      const firstDay = dayjs(interval.startDate).utc().startOf('day').valueOf();
      const lastDay = dayjs(dayjs(interval.endDate).valueOf()).add(dayjs().utcOffset(), 'minutes').valueOf();

      const yAxisConfig: Highcharts.YAxisOptions[] = [
        {
          ...getYAxis('Porciones de frío acumuladas', hideYValues),
          min: yAxisLimits.min,
          max: yAxisLimits.max,
          id: 'chill-portions',
        } as Highcharts.YAxisOptions,
      ];

      return ({
        ...getChartConfig(
          chartData,
          height,
          hideXValues,
          undefined,
          dayjs(interval.startDate).valueOf(),
          dayjs(interval.endDate).valueOf(),
        ),
        yAxis: yAxisConfig,
        plotOptions: {
          series: {
            marker: {
              enabled: true,
            },
          },
        },
        xAxis: {
          type: 'datetime',
          min: firstDay,
          max: lastDay,
          labels: {
            format: '{value:%b %d}',
            enabled: !hideXValues,
          },
          plotLines: (() => {
            const lines: any[] = [];
            let currentDay = '';

            if (chartData.absoluteCategories) {
              chartData.absoluteCategories.forEach((dateStr: string, index: number) => {
                const date = dayjs(dateStr).utc().startOf('day');
                const day = date.format('YYYY-MM-DD');

                if (day !== currentDay && index > 0) {
                  lines.push({
                    color: '#999999',
                    width: 1,
                    dashStyle: 'Dash',
                    value: date.valueOf(),
                  });
                  currentDay = day;
                }
              });
            }
            return lines;
          })(),
          plotBands: getPlotBands(chartData.absoluteCategories),
        },
      });
    },
    [
      height,
      hideXValues,
      chartData.absoluteCategories,
      hideYValues,
      interval,
      chartData.series,
    ],
  );

  const handleConfigClick = useChartConfigClick({
    chartView,
    route: chartName,
    farmId,
    sectorId,
    onFullScreen: () => setConfigDialogOpen(true),
  });

  const handleFullScreenClick = () => {
    navigateToChart(`fullScreen/${chartName}`, history, {
      farmId,
      sectorId,
    });
  };

  const showInitialSpinner = isLoading && !chartData.series.length;

  return (
    <>
      <div>
        {showInitialSpinner ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={height}>
            <CircularProgress />
          </Box>
        ) : (
          <BaseHighchart
            chartView={chartView}
            sectorId={sectorId}
            filter={chartView === 'settings' ? <ChartFilter sectorId={sectorId} /> : undefined}
            title={title}
            yAxisTitle={yAxisTitle}
            series={chartData.series as Highcharts.SeriesOptionsType[]}
            hideYValues={hideYValues}
            showFullScreen={true}
            legend={{ enabled: !hideLegend }}
            chartConfig={chartConfig}
            onConfigClick={handleConfigClick}
            onFullScreenClick={handleFullScreenClick}
            absoluteCategories={chartData.absoluteCategories}
            height={height}
            zoomSync={true}
            autoAdjustYAxis={true}
            interval={interval}
            graphName={CHILL_PORTIONS_NAME}
          />
        )}
      </div>
      <ChartConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        title="Configuración · Porciones de frío"
      >
        <ChillPortionsConfigContent
          sectorId={sectorId}
          onConfigSaved={() => loadConfig()}
        />
      </ChartConfigDialog>
    </>
  );
}

export default ChillPortionsChart;
